"use client";

import * as React from "react";
import Link from "next/link";
import { type VariantProps } from "class-variance-authority";

import { cn } from "@/src/lib/utils";
import { buttonVariants } from "./buttonVariants";

import "./button.css";
import { ButtonProps } from "./Button.types";

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      href,
      target,
      rel,
      leftIcon,
      rightIcon,
      unstyled,
      children,
      id,
      style,
      tabIndex,
      onClick,
      "aria-hidden": ariaHidden,
      "aria-label": ariaLabel,
      "aria-expanded": ariaExpanded,
      ...props
    },
    ref,
  ) => {
    // Link's props are typed for HTMLAnchorElement, so the leftover
    // ButtonHTMLAttributes<HTMLButtonElement> bag in `props` (event handlers
    // typed for the wrong element, button-only attrs like `type`/`disabled`)
    // can't be spread onto it wholesale — only forward the universal subset
    // that's valid (and meaningful) on both elements. onClick is cast
    // separately for the Link branches since its type is pinned to
    // HTMLButtonElement by ButtonProps; the handler itself works fine on
    // either element at runtime (React's MouseEvent is structurally the
    // same regardless of currentTarget's element type for typical usage).
    const sharedProps = { id, style, tabIndex, "aria-hidden": ariaHidden, "aria-label": ariaLabel, "aria-expanded": ariaExpanded };
    const linkOnClick = onClick as unknown as React.MouseEventHandler<HTMLAnchorElement> | undefined;

    if (unstyled) {
      if (href) {
        return (
          <Link
            href={href}
            target={target}
            rel={rel}
            className={cn(className)}
            onClick={linkOnClick}
            {...sharedProps}
          >
            {children}
          </Link>
        );
      }

      return (
        <button ref={ref} className={cn(className)} onClick={onClick} {...sharedProps} {...props}>
          {children}
        </button>
      );
    }

    const isCurtain =
      variant === undefined || variant === "primary" || variant === "secondary";
    const isLinkLike = variant === "link" || variant === "link-plain";

    const label = (
      <>
        {leftIcon}

        {isLinkLike ? <span className="btn-link-text">{children}</span> : children}

        {isLinkLike ? (
          <span className="btn-link-icon inline-flex">{rightIcon}</span>
        ) : (
          rightIcon
        )}
      </>
    );

    const content = isCurtain ? (
      <>
        <span className="btn-face-default inline-flex items-center self-stretch gap-2">
          {label}
        </span>

        <span
          className="btn-face-hover absolute inset-0 inline-flex items-center justify-center gap-2 rounded-[inherit]"
          aria-hidden="true"
        >
          {label}
        </span>
      </>
    ) : (
      <span className="btn-content inline-flex items-center gap-2">
        {label}
      </span>
    );

    const variantClass =
      variant === "link"
        ? "btn-link"
        : variant === "link-plain"
          ? "btn-link-plain"
          : variant === "secondary"
            ? "btn-secondary"
            : variant === "ghost"
              ? "btn-ghost"
              : variant === "admin-primary"
                ? "btn-admin-primary"
                : variant === "admin-secondary"
                  ? "btn-admin-secondary"
                  : "btn-primary";

    const classes = cn(buttonVariants({ variant, size }), variantClass, className);

    if (href) {
      return (
        <Link
          href={href}
          target={target}
          rel={rel}
          className={classes}
          onClick={linkOnClick}
          {...sharedProps}
        >
          {content}
        </Link>
      );
    }

    return (
      <button ref={ref} className={classes} onClick={onClick} {...sharedProps} {...props}>
        {content}
      </button>
    );
  },
);

Button.displayName = "Button";

export { Button };
